import type { CircleRadius } from '../useMapCircleRadius.types';
import { getCircleRadius } from './get-circle-radius';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates points around the circle's perimeter for fitting the map bounds.
 * Uses the actual circle geometry to ensure the entire circle is visible.
 *
 * @param circleRadius - The circle radius configuration
 * @param numPoints - Number of points to generate around the perimeter (default: 16)
 * @returns Array of coordinates representing points around the circle perimeter
 */
export function getCirclePerimeterPoints(
  circleRadius: CircleRadius,
  numPoints = 16,
): CirclePerimeterPoints {
  const { centerLat, centerLng } = circleRadius;
  const radiusInMiles = getCircleRadius(circleRadius);

  if (!radiusInMiles) {
    return [];
  }

  // Convert radius from miles to degrees

  const latRadius = radiusInMiles / MILES_PER_DEGREE_LATITUDE;
  const lngRadius =
    radiusInMiles /
    (MILES_PER_DEGREE_LATITUDE * Math.cos(centerLat * RADIANS_PER_DEGREE));

  // Generate points around the circle perimeter
  const createPerimeterPointReducer = createPerimeterPointReducerFactory({
    centerLat,
    centerLng,
    latRadius,
    lngRadius,
    numPoints,
  });

  const pointEntries = Array.from({ length: numPoints });

  return pointEntries.reduce<CirclePerimeterPoints>(createPerimeterPointReducer, []); // prettier-ignore
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function createPerimeterPointReducerFactory(
  params: CreatePerimeterPointReducerParams,
) {
  const { centerLat, centerLng, latRadius, lngRadius, numPoints } = params;

  return (
    points: CirclePerimeterPoints,
    _: unknown,
    i: number,
  ): CirclePerimeterPoints => {
    const angle = (i * 2 * Math.PI) / numPoints;

    const latOffset = latRadius * Math.sin(angle);
    const lngOffset = lngRadius * Math.cos(angle);

    const point: CirclePerimeterPoint = {
      latitude: centerLat + latOffset,
      longitude: centerLng + lngOffset,
    };

    return [...points, point];
  };
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MILES_PER_DEGREE_LATITUDE = 69;
const RADIANS_PER_DEGREE = Math.PI / 180;

// ─── Types ───────────────────────────────────────────────────────────────────

type CirclePerimeterPoint = {
  latitude: number;
  longitude: number;
};

type CirclePerimeterPoints = Array<CirclePerimeterPoint>;

type CreatePerimeterPointReducerParams = {
  centerLat: number;
  centerLng: number;
  latRadius: number;
  lngRadius: number;
  numPoints: number;
};
